
// Receipt voucher status types
export type ReceiptVoucherStatus = 'draft' | 'completed' | 'cancelled';

// Receipt voucher type - now links to category code
// Type is now a string (category code) instead of fixed union
export type ReceiptVoucherType = string;

// Payment method options
export type PaymentMethod = 'cash' | 'transfer';

// Customer response interface (matching backend DTO)
export interface CustomerResponse {
  id: string;
  code: string;
  name: string;
  gender?: string;
  birth?: string;
  phone: string;
  email?: string;
  facebook?: string;
  company?: string;
  tax?: string;
  address?: {
    province?: string;
    city?: string;
    district?: string;
    commune?: string;
    street_address?: string;
  };
  note?: string;
  debit?: number;
  total_sell?: number;
  createdAt?: string;
  updatedAt?: string;
}

// Status history entry
export interface StatusHistoryEntry {
  status: ReceiptVoucherStatus;
  note?: string;
  created_at: string;
  created_by: string;
}

// Base receipt voucher interface
export interface ReceiptVoucher {
  id: string;
  code: string;
  receipt_type: ReceiptVoucherType;
  customer_id: string;
  payment_method: PaymentMethod;
  account_number?: string;
  transfer_note?: string;
  note?: string;
  invoice_ids: string[];
  received_now: number;
  current_status: StatusHistoryEntry;
  status_history: StatusHistoryEntry[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

// UI-specific receipt voucher interface with additional computed fields
export interface ReceiptVoucherUI extends ReceiptVoucherResponse {
  // Additional computed fields for UI display
  invoice_count?: number;
  status_display?: string;
}

// API Request types
export interface CreateReceiptVoucherRequest {
  receipt_type: ReceiptVoucherType;
  customer_id?: string; // Optional - only required for customer_payment type
  payment_method: PaymentMethod;
  account_number?: string;
  transfer_note?: string;
  note?: string;
  reason?: string;
  received_now: number;
  invoice_ids?: string[]; // Invoice codes that this receipt voucher applies to
  created_at?: string; // Optional custom creation timestamp (RFC3339 format)
}

export interface UpdateReceiptVoucherRequest {
  receipt_type?: ReceiptVoucherType;
  customer_id?: string; // Optional - only required for customer_payment type
  payment_method?: PaymentMethod;
  account_number?: string;
  transfer_note?: string;
  note?: string;
  reason?: string;
  received_now?: number;
  invoice_ids?: string[]; // Invoice codes that this receipt voucher applies to
}

// API Response types
export interface StatusHistoryResponse {
  status: ReceiptVoucherStatus;
  note?: string;
  created_at: string;
  created_by: string;
}

export interface ReceiptVoucherResponse {
  id: string;
  code: string;
  customer_id: string;
  receipt_type: ReceiptVoucherType;
  receipt_type_name: string; // Category name for display
  payment_method: PaymentMethod;
  account_number?: string;
  transfer_note?: string;
  note?: string;
  reason?: string;
  invoice_ids: string[];
  invoices?: InvoiceResponse[]; // Full invoice details
  received_now: number;
  current_status: StatusHistoryResponse;
  status_history: StatusHistoryResponse[];
  // Customer information (nested object from API)
  customer?: CustomerResponse;
  created_by: string;
  created_by_name: string;
  createdAt: string;
  updated_at: string;
}

// Receipt voucher with invoices response
export interface ReceiptVoucherWithInvoicesResponse {
  receipt_voucher: ReceiptVoucherResponse;
  invoices: InvoiceResponse[];
}

// Import invoice response type (assuming it exists)
export interface InvoiceResponse {
  id: string;
  code: string;
  order_id: string;
  note?: string;
  price: number;
  discount: number;
  total: number;
  customer_paid: number;
  current_status: StatusHistoryResponse;
  status_history: StatusHistoryResponse[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

// List query filters
export interface ReceiptVoucherFilters {
  receipt_type?: ReceiptVoucherType;
  payment_method?: PaymentMethod;
  payer_name?: string;
  customer_id?: string;
  status?: ReceiptVoucherStatus;
  date_from?: string;
  date_to?: string;
}

export interface ReceiptVoucherListQuery extends ReceiptVoucherFilters {
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  customer_id?: string;
}

// Paginated response
export interface PaginatedReceiptVoucherResponse {
  data: ReceiptVoucherResponse[];
  total: number;
  page: number;
  page_size: number;
}

// Status counts for filter tabs
export interface ReceiptVoucherStatusCounts {
  all: number;
  draft: number;
  confirmed: number;
  completed: number;
  cancelled: number;
}

// Form validation types
export interface ReceiptVoucherFormErrors {
  receipt_type?: string;
  customer_id?: string;
  payment_method?: string;
  account_number?: string;
  transfer_note?: string;
  received_now?: string;
}

// DEPRECATED: Static receipt voucher types - now fetched from categories API
// These constants are kept for backward compatibility but should be replaced by category fetching
export const RECEIPT_VOUCHER_TYPES: { value: ReceiptVoucherType; label: string }[] = [
  { value: 'fund_deposit', label: 'receiptVoucher.types.fund_deposit' },
  { value: 'customer_payment', label: 'receiptVoucher.types.customer_payment' }
];

// Category types that represent receipt voucher types
export const RECEIPT_VOUCHER_CATEGORY_TYPE_CUSTOMER = 'receipt_voucher_type_customer';
export const RECEIPT_VOUCHER_CATEGORY_TYPE_OTHER = 'receipt_voucher_type_other';

export const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'receiptVoucher.paymentMethods.cash' },
  { value: 'transfer', label: 'receiptVoucher.paymentMethods.transfer' }
];

export const RECEIPT_VOUCHER_STATUS_OPTIONS: { value: ReceiptVoucherStatus; label: string }[] = [
  { value: 'draft', label: 'receiptVoucher.status.draft' },
  { value: 'completed', label: 'receiptVoucher.status.completed' },
  { value: 'cancelled', label: 'receiptVoucher.status.cancelled' }
];
